import { API_CONFIG, ApiError } from '@/config/api';

// Get token from localStorage
const getAuthToken = () => {
  return localStorage.getItem('indispare_token');
};

// Generic API response wrapper
export interface ApiResponse<T> {
  status: boolean;
  message: string;
  data: T;
}

// Fetch options interface
interface FetchOptions extends RequestInit {
  timeout?: number;
  skipAuth?: boolean; // Skip adding auth token for certain requests
}

/**
 * Core API client with reusable fetch functionality
 */
class ApiClient {
  private baseURL: string;
  private defaultTimeout: number;

  constructor(baseURL: string, timeout: number = API_CONFIG.TIMEOUT) {
    this.baseURL = baseURL;
    this.defaultTimeout = timeout;
  }

  /**
   * Generic fetch method with timeout and error handling
   */
  private async fetch<T>(
    endpoint: string,
    options: FetchOptions = {}
  ): Promise<T> {
    const { timeout = this.defaultTimeout, skipAuth = false, ...fetchOptions } = options;
    const url = `${this.baseURL}${endpoint}`;

    console.log(`API Request: ${fetchOptions.method || 'GET'} ${url}`);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      console.warn(`Request timeout after ${timeout}ms: ${url}`);
      controller.abort();
    }, timeout);

    // Prepare headers
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...fetchOptions.headers,
    };

    // Add auth token if available and not skipped
    if (!skipAuth) {
      const token = getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    try {
      const response = await fetch(url, {
        ...fetchOptions,
        headers,
        signal: controller.signal,
        credentials: 'include', // Include cookies in cross-origin requests
      });

      clearTimeout(timeoutId);
      console.log(`API Response: ${response.status} ${url}`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new ApiError(
          errorData.message || `HTTP error! status: ${response.status}`,
          response.status,
          errorData
        );
      }

      const data = await response.json();
      return data;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof ApiError) {
        throw error;
      }

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          console.error(`Request aborted (timeout): ${url}`);
          throw new ApiError('Request timeout');
        }
        console.error(`Request failed: ${url}`, error.message);
        throw new ApiError(error.message);
      }

      console.error(`Unknown error for: ${url}`, error);
      throw new ApiError('An unknown error occurred');
    }
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, options?: FetchOptions): Promise<T> {
    return this.fetch<T>(endpoint, {
      ...options,
      method: 'GET',
    });
  }

  /**
   * POST request
   */
  async post<T>(
    endpoint: string,
    data?: any,
    options?: FetchOptions
  ): Promise<T> {
    return this.fetch<T>(endpoint, {
      ...options,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * PUT request
   */
  async put<T>(
    endpoint: string,
    data?: any,
    options?: FetchOptions
  ): Promise<T> {
    return this.fetch<T>(endpoint, {
      ...options,
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string, options?: FetchOptions): Promise<T> {
    return this.fetch<T>(endpoint, {
      ...options,
      method: 'DELETE',
    });
  }
}

// Create and export the default API client instance
export const apiClient = new ApiClient(API_CONFIG.BASE_URL);
export const apiClient1 = new ApiClient(API_CONFIG.DEFAULT_IMAGE_BASE_URL);

// Export for testing or custom instances
export { ApiClient };
